import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { DropdownStyles } from "../../types/styles";
import Icon, { IconType } from "../Icon/Icon";

const Container = styled.div`
  display: flex;
  gap: 5px;
  flex-direction: column;
  width: 100%;
  font-size: 13px;
`;

const InputAndIconContainer = styled.div<{
  styles: DropdownStyles;
  isErrored?: boolean;
  rightBorderRadiusOnly?: boolean;
}>`
  display: flex;
  gap: 4px;
  align-items: center;
  border: 1px solid
    ${({ styles, isErrored }) =>
      isErrored
        ? styles.DropdownGeneralValidationErrorColour
        : styles.DropdownBorderColour};
  border-radius: ${({ rightBorderRadiusOnly, styles }) =>
    rightBorderRadiusOnly
      ? `0 ${styles.DropdownBorderRadius}px ${styles.DropdownBorderRadius}px 0`
      : `${styles.DropdownBorderRadius}px`};
  background-color: ${({ styles }) => styles.DropdownGeneralBackgroundColour};
  padding: 4px 8px 4px 8px;
  height: 40px;
  box-sizing: border-box;
  color: ${({ styles }) => styles.DropdownGeneralTextColour};

  &:focus-within {
    outline: ${({ styles }) => `1px solid ${styles.DropdownBorderFocusColour}`};
    border: ${({ styles }) => `1px solid ${styles.DropdownBorderFocusColour}`};
  }
`;

const StyledInput = styled.input<{
  styles: DropdownStyles;
}>`
  border: none;
  width: 100%;
  background: none;
  font-family: inherit;
  color: ${({ styles }) => styles.DropdownGeneralTextColour};
  &:focus {
    border: none;
    outline: none;
  }

  &::placeholder {
    color: ${({ styles }) => styles.DropdownGeneralPlaceholderColour};
  }

  // Remove built in calendar icon and up/down arrow icons.
  &::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }
  &::-webkit-inner-spin-button {
    display: none;
    -webkit-appearance: none;
  }
`;

const StyledIcon = styled(Icon)<{
  styles: DropdownStyles;
}>`
  width: 16px;
  height: 16px;
  margin: 0 8px 1px 6px;
  color: ${({ styles }) => styles.DropdownGeneralTextColour};
`;

const StyledLabel = styled.label<{
  styles: DropdownStyles;
}>`
  font-weight: 700;
  color: ${({ styles }) => styles.OverallGeneralTextColour};
`;

const StyledSpan = styled.span`
  font-weight: normal;
  margin-left: 4px;
`;

const ErrorMessage = styled.div<{
  styles: DropdownStyles;
}>`
  color: ${({ styles }) => styles.DropdownGeneralValidationErrorColour};
`;

export type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange"
> & {
  onChange: (value: string | number) => void;
  label?: string;
  leftIcon?: IconType;
  value?: string | number;
  bold?: boolean;
  optional?: boolean;
  styles: DropdownStyles;
  /**
   * Error message to display for the input and used to change border style.
   * This message will be displayed if there is an error associated with the input.
   */
  errorMessage?: string;
  /**
   * Should be set to true if the input is errored but error message
   * should not be displayed.
   */
  isErrored?: boolean;
  /**
   * Should be passed in if there is no label.
   */
  ariaLabel?: string;
  /**
   * If true, the input will only have a border radius on the right side.
   * This is used for the MobileInput component.
   */
  rightBorderRadiusOnly?: boolean;
  dataId?: string;
};

export const Input: React.FC<InputProps> = ({
  onChange,
  label,
  leftIcon,
  value,
  optional,
  styles,
  errorMessage,
  isErrored,
  ariaLabel,
  rightBorderRadiusOnly,
  dataId,
  ...props
}) => {
  const { t } = useTranslation();
  return (
    <Container data-id={dataId + "-container"}>
      {label && (
        <StyledLabel htmlFor={label} styles={styles}>
          {label}
          {optional && <StyledSpan>({t("Optional")})</StyledSpan>}
        </StyledLabel>
      )}
      <InputAndIconContainer
        styles={styles}
        isErrored={isErrored || !!errorMessage}
        rightBorderRadiusOnly={rightBorderRadiusOnly}
      >
        {leftIcon && (
          <StyledIcon
            icon={leftIcon}
            styles={styles}
            color={styles.DropdownGeneralTextColour}
          />
        )}
        <StyledInput
          {...props}
          id={label}
          onChange={(event) => onChange(event.target.value)}
          value={value}
          styles={styles}
          aria-label={label ?? ariaLabel}
          data-id={dataId + "-input"}
        />
      </InputAndIconContainer>
      {errorMessage && (
        <ErrorMessage styles={styles} data-id={dataId + "-error"}>
          {errorMessage}
        </ErrorMessage>
      )}
    </Container>
  );
};

export default Input;
